import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronRight, Plus, Key, Building2, Wallet, Settings, Trash2, Edit2, CheckCircle2, XCircle, ArrowLeft } from 'lucide-react';
import { MOCK_PROVIDERS, formatCurrency, getInitials, getProviderTotals } from '../data/providers';
import { useScreenInit } from '../useScreenInit.js';
export function ProviderDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const screenInit = useScreenInit();
  const [activeEnv, setActiveEnv] = useState(screenInit.activeEnv ?? 'Produzione');
  const provider = MOCK_PROVIDERS.find(p => p.id === id);
  if (!provider) {
    return <div className="min-h-screen bg-[#F4F6F8] p-4 md:p-8 flex items-center justify-center flex-col" data-id="element-4094" data-mp="3nareik40cquf">
        <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-4095" data-mp="xjcmaksm0fah">
          Provider non trovato
        </h2>
        <button onClick={() => navigate('/providers')} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors" data-id="element-4096" data-mp="12h4gjlgq8dhu">
          <ArrowLeft className="w-4 h-4" data-id="element-4097" data-mp="eerhb3wr5xm2" />
          Torna ai Provider
        </button>
      </div>;
  }
  const {
    totalBudget,
    totalSpent,
    percent
  } = getProviderTotals(provider);
  return <div className="min-h-screen bg-[#F4F6F8] p-4 md:p-8" data-id="element-4098" data-mp="5i8odroiu9ph">
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-2 mb-6 text-[13px]" data-id="element-4099" data-mp="i473zfshay7">
        <Link to="/providers" className="text-gray-500 hover:text-[#1A202C] transition-colors font-medium" data-id="element-4100" data-mp="3ruflldfkbijy">
          Provider
        </Link>
        <ChevronRight className="w-3.5 h-3.5 text-gray-400" data-id="element-4101" data-mp="3iv2cilvcovzr" />
        <span className="text-[#1A202C] font-semibold" data-id="element-4102" data-mp="3iwn360ejnusc">{provider.name}</span>
      </nav>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col" data-id="element-4103" data-mp="3dv63g87sqedc">
        {/* Header */}
        <div className="px-6 py-6 border-b border-gray-100 flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-id="element-4104" data-mp="2z90gf7kfdxdl">
          <div className="flex items-center gap-4" data-id="element-4105" data-mp="3494yh20zf05x">
            <div className="w-16 h-16 rounded-xl flex items-center justify-center border border-gray-100 shadow-sm bg-white overflow-hidden flex-shrink-0" style={{
            backgroundColor: provider.logo ? 'white' : `${provider.color}15`
          }} data-id="element-4106" data-mp="345qwhuw8csz0">
              {provider.logo ? <img src={provider.logo} alt={provider.name} className="w-10 h-10 object-contain" data-id="element-4107" data-mp="3mxtf3bsfmjbw" /> : <span className="text-[20px] font-bold" style={{
              color: provider.color
            }} data-id="element-4108" data-mp="3d775by7ecn14">
                  {getInitials(provider.name)}
                </span>}
            </div>
            <div data-id="element-4109" data-mp="1u92a9z1ttmm0">
              <div className="flex items-center gap-3 mb-1.5" data-id="element-4110" data-mp="1u8ixewr19oeu">
                <h1 className="text-[22px] font-heading font-semibold text-[#1A202C] tracking-tight" data-id="element-4111" data-mp="1pbskgiaiz8xn">
                  {provider.name}
                </h1>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${provider.status === 'Active' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-100 text-gray-600 border border-gray-200'}`} data-id="element-4112" data-mp="241q3ykxifagi">
                  {provider.status === 'Active' ? <CheckCircle2 className="w-3.5 h-3.5" data-id="element-4113" data-mp="1kcz4mj7rn5ub" /> : <XCircle className="w-3.5 h-3.5" data-id="element-4114" data-mp="1ga2uzi07ek2i" />}
                  {provider.status === 'Active' ? 'Attivo' : 'Inattivo'}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5" data-id="element-4115" data-mp="2rq0xt44sg9va">
                {provider.models.map(model => <span key={model} className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-gray-50 text-gray-600 border border-gray-200" data-id="element-4116" data-mp="3n0689t0ctwal">
                    {model}
                  </span>)}
              </div>
            </div>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-[#1A202C] hover:bg-gray-100 rounded-lg transition-colors text-[13px] font-medium border border-gray-200 shadow-sm" data-id="element-4117" data-mp="4vcohikhn39j">
            <Settings className="w-4 h-4" data-id="element-4118" data-mp="dsmyz1pheode" />
            Impostazioni Provider
          </button>
        </div>

        {/* Summary Stats */}
        <div className="px-6 py-5 border-b border-gray-100 grid grid-cols-1 md:grid-cols-3 gap-6 bg-gray-50/50" data-id="element-4119" data-mp="nl2ezth7x2vn">
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm" data-id="element-4120" data-mp="skf4yn3dyq2g">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 font-medium" data-id="element-4121" data-mp="xi7687px7ojb">
              Budget Totale
            </p>
            <p className="text-[24px] font-semibold text-[#1A202C]" data-id="element-4122" data-mp="2i2c95bayb0ni">
              {formatCurrency(totalBudget)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm" data-id="element-4123" data-mp="282bmbfdvi2ek">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 font-medium" data-id="element-4124" data-mp="284ekimp6j1ud">
              Spesa Corrente
            </p>
            <p className="text-[24px] font-semibold text-[#1A202C]" data-id="element-4125" data-mp="30q7godfyzi86">
              {formatCurrency(totalSpent)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm" data-id="element-4126" data-mp="2vu8xr5deusf5">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 font-medium" data-id="element-4127" data-mp="2qzmmd27bwzv2">
              Utilizzo Budget
            </p>
            <div className="flex items-end gap-3" data-id="element-4128" data-mp="3jhgtavl1en3z">
              <p className={`text-[24px] font-semibold leading-none ${percent >= 80 ? 'text-amber-600' : 'text-[#1A202C]'}`} data-id="element-4129" data-mp="3jjbgyhnp0tbr">
                {percent}%
              </p>
              <div className="w-full bg-gray-100 rounded-full h-2 mb-1.5 flex-1" data-id="element-4130" data-mp="1cu9pke2t946i">
                <div className={`h-2 rounded-full transition-all ${percent >= 80 ? 'bg-amber-500' : 'bg-[#00B4D8]'}`} style={{
                width: `${percent}%`
              }} data-id="element-4131" data-mp="1hr9lvx9uyskf" />
              </div>
            </div>
          </div>
        </div>

        {/* Environment Tabs + Budget Lines */}
        <div className="px-6 py-6 flex-1" data-id="element-4132" data-mp="y4bxhn1rfuc1">
          {/* Environment Tabs */}
          <div className="border-b-2 border-gray-200 mb-6" data-id="element-4133" data-mp="y28xca6w8ktl">
            <div className="-mb-[2px] flex items-center gap-6 overflow-x-auto custom-scrollbar" data-id="element-4134" data-mp="k642hkoab33i">
              {['Produzione', 'Staging', 'Development'].map(env => <button key={env} onClick={() => setActiveEnv(env)} className={`pb-3 text-[14px] font-medium transition-colors whitespace-nowrap border-b-2 ${activeEnv === env ? 'border-[#00B4D8] text-[#2A3B5C]' : 'border-transparent text-gray-500 hover:text-gray-800'}`} data-id="element-4135" data-mp="2eibw8uovddxp">
                  {env}
                </button>)}
            </div>
          </div>

          <div className="flex items-center justify-between mb-6" data-id="element-4136" data-mp="hlpb4r5x8oxi">
            <h3 className="text-[14px] font-semibold text-[#1A202C] uppercase tracking-wider" data-id="element-4137" data-mp="cteebx6h9j96">
              Linee di Budget — {activeEnv} ({provider.budgetLines.length})
            </h3>
            <button className="flex items-center gap-2 px-4 py-2 bg-[#2A3B5C] text-white rounded-lg hover:bg-[#1E2A40] transition-colors shadow-sm" data-id="element-4138" data-mp="lmho9vhwzdz2">
              <Plus className="w-4 h-4" data-id="element-4139" data-mp="10ih09h859y9h" />
              <span className="text-[13px] font-medium" data-id="element-4140" data-mp="vjmatwfo9bge">Aggiungi Linea</span>
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-4141" data-mp="329tw7gs4wvte">
            {provider.budgetLines.map(line => {
            const percentSpent = Math.min(100, Math.round(line.spent / line.budget * 100));
            const isNearLimit = percentSpent >= 80;
            return <div key={line.id} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow" data-id="element-4142" data-mp="2gfldid9xnz40">
                  <div className="flex items-start justify-between mb-4" data-id="element-4143" data-mp="2q6g2vqih7eto">
                    <div data-id="element-4144" data-mp="1skioa477vtn0">
                      <div className="flex items-center gap-2 mb-1.5" data-id="element-4145" data-mp="1slbosnfl1mwp">
                        <h4 className="text-[15px] font-semibold text-[#1A202C]" data-id="element-4146" data-mp="1xjn258536fjo">
                          {line.name}
                        </h4>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${line.type === 'Interno' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-purple-50 text-purple-700 border border-purple-200'}`} data-id="element-4147" data-mp="22k15wdznn3bw">
                          {line.type}
                        </span>
                      </div>
                      {line.fornitore && <p className="text-[12px] text-gray-500 flex items-center gap-1.5" data-id="element-4148" data-mp="12ajdkqosgg77">
                          <Building2 className="w-3.5 h-3.5" data-id="element-4149" data-mp="xhymhwx9oh7d" />
                          Fornitore: {line.fornitore}
                        </p>}
                    </div>
                    <div className="flex items-center gap-1" data-id="element-4150" data-mp="nkuagpx4y24a">
                      <button title="Modifica" className="p-1.5 text-gray-400 hover:text-[#00B4D8] hover:bg-gray-50 rounded-md transition-colors" data-id="element-4151" data-mp="ni7srxgj668c">
                        <Edit2 className="w-4 h-4" data-id="element-4152" data-mp="9l2qm8jxtrid" />
                      </button>
                      <button title="Elimina" className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-gray-50 rounded-md transition-colors" data-id="element-4153" data-mp="9n5m383v0cuh">
                        <Trash2 className="w-4 h-4" data-id="element-4154" data-mp="3w0b3afu6nqph" />
                      </button>
                    </div>
                  </div>

                  {/* API Key */}
                  <div className="flex items-center gap-2 mb-5 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 w-fit" data-id="element-4155" data-mp="3r3knltmaix4l">
                    <Key className="w-4 h-4 text-gray-400" data-id="element-4156" data-mp="3x0dc16t4chs" />
                    <span className="text-[12px] font-mono text-gray-600" data-id="element-4157" data-mp="3xjvnj0dd2wc">
                      {line.apiKeyMasked}
                    </span>
                  </div>

                  {/* Budget Progress */}
                  <div data-id="element-4158" data-mp="388vwa9rgol7w">
                    <div className="flex items-center justify-between text-[12px] mb-2" data-id="element-4159" data-mp="3m23mpy3t3ao0">
                      <span className="text-gray-500 flex items-center gap-1.5 font-medium" data-id="element-4160" data-mp="3hbna2g7t7t2p">
                        <Wallet className="w-3.5 h-3.5" data-id="element-4161" data-mp="3h9u0as1wgsqh" />
                        Spesa mensile
                      </span>
                      <span className="font-semibold text-[#1A202C]" data-id="element-4162" data-mp="2onj2fp5oh5xd">
                        {formatCurrency(line.spent)} /{' '}
                        {formatCurrency(line.budget)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-4163" data-mp="18x8dr9hhhgkv">
                      <div className={`h-2 rounded-full transition-all ${isNearLimit ? 'bg-amber-500' : 'bg-[#00B4D8]'}`} style={{
                    width: `${percentSpent}%`
                  }} data-id="element-4164" data-mp="v51zxu1szlol" />
                    </div>
                    <div className="flex justify-end mt-1.5" data-id="element-4165" data-mp="1rti1koi6tli2">
                      <span className={`text-[11px] font-medium ${isNearLimit ? 'text-amber-600' : 'text-gray-400'}`} data-id="element-4166" data-mp="1duqmozvi3vsv">
                        {percentSpent}% utilizzato
                      </span>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </div>;
}